import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LogOut } from "lucide-react";
import { SunMark } from "../components/Logo";
import { TripDesigner } from "../components/TripDesigner";
import { api, formatMoney } from "../lib/api";

const login = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const STATUS = { paid: "text-emerald-300", pending: "text-[#E6C594]", expired: "text-[#8F877A]", failed: "text-red-300", refunded: "text-sky-300" };

const Payments = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/admin/payments").then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch(() => setRows([]));
  }, []);
  return (
    <div className="border border-[#D4AF37]/20 bg-[#14120E]" data-testid="admin-payments">
      <p className="px-5 py-4 border-b border-[#D4AF37]/15 font-mono text-[11px] uppercase tracking-[0.25em] text-[#D4AF37]">Acconti ricevuti</p>
      <div className="overflow-x-auto max-h-[580px] overflow-y-auto" data-lenis-prevent>
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wider text-[#B5AC9C]">
            <tr>{["Data", "Tour", "Cliente", "Importo", "Stato"].map((h) => <th key={h} className="px-5 py-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows === null && <tr><td className="px-5 py-6 text-[#B5AC9C]" colSpan={5}>Caricamento…</td></tr>}
            {rows?.length === 0 && <tr><td className="px-5 py-6 text-[#B5AC9C]" colSpan={5} data-testid="admin-payments-empty">Nessun pagamento ancora.</td></tr>}
            {rows?.map((r) => (
              <tr key={r.session_id} className="border-t border-[#D4AF37]/10" data-testid="admin-payment-row">
                <td className="px-5 py-3 whitespace-nowrap text-[#CFC6B6]">{new Date(r.created_at).toLocaleString("it-IT")}</td>
                <td className="px-5 py-3">{r.lookup_key === "deposit_4x4" ? "4X4" : "Moto"}</td>
                <td className="px-5 py-3 text-[#CFC6B6]">{r.customer_name || "—"}<br /><span className="text-xs">{r.customer_email || ""} {r.customer_phone || ""}</span></td>
                <td className="px-5 py-3 whitespace-nowrap">{formatMoney(r.amount, r.currency, "it-IT")}</td>
                <td className={`px-5 py-3 ${STATUS[r.payment_status] || ""}`}>{r.payment_status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TOUR = { moto: "Moto", "4x4": "4X4", both: "Moto + 4X4" };

const Quotes = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/admin/quotes").then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch(() => setRows([]));
  }, []);
  return (
    <div className="border border-[#D4AF37]/20 bg-[#14120E] lg:col-span-2" data-testid="admin-quotes">
      <p className="px-5 py-4 border-b border-[#D4AF37]/15 font-mono text-[11px] uppercase tracking-[0.25em] text-[#D4AF37]">Richieste di preventivo</p>
      <div className="overflow-x-auto max-h-[480px] overflow-y-auto" data-lenis-prevent>
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-wider text-[#B5AC9C]">
            <tr>{["Data", "Cliente", "Tour", "Date viaggio", "Pers.", "Note", "Email"].map((h) => <th key={h} className="px-5 py-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows === null && <tr><td className="px-5 py-6 text-[#B5AC9C]" colSpan={7}>Caricamento…</td></tr>}
            {rows?.length === 0 && <tr><td className="px-5 py-6 text-[#B5AC9C]" colSpan={7} data-testid="admin-quotes-empty">Nessuna richiesta ancora.</td></tr>}
            {rows?.map((r) => (
              <tr key={r.quote_id} className="border-t border-[#D4AF37]/10 align-top" data-testid="admin-quote-row">
                <td className="px-5 py-3 whitespace-nowrap text-[#CFC6B6]">{new Date(r.created_at).toLocaleString("it-IT")}<br /><span className="text-xs uppercase">{r.lang}</span></td>
                <td className="px-5 py-3 text-[#F7F4EE]">{r.name}<br /><span className="text-xs text-[#CFC6B6] break-all">{r.contact}</span></td>
                <td className="px-5 py-3 whitespace-nowrap">{TOUR[r.tour] || r.tour}</td>
                <td className="px-5 py-3 whitespace-nowrap text-[#CFC6B6]">{r.date_from}{r.date_to ? ` → ${r.date_to}` : ""}</td>
                <td className="px-5 py-3">{r.people}</td>
                <td className="px-5 py-3 text-xs text-[#B5AC9C] max-w-xs whitespace-pre-wrap">{r.notes || "—"}</td>
                <td className={`px-5 py-3 whitespace-nowrap ${r.email_sent ? "text-emerald-300" : "text-[#E6C594]"}`}>{r.email_sent ? "inviata" : "non inviata"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function Admin() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user ?? null);
  const [auth, setAuth] = useState(location.state?.user ? true : null);
  const denied = location.state?.denied;

  useEffect(() => {
    if (location.state?.user) return;
    api.get("/auth/me").then((r) => { setUser(r.data); setAuth(true); }).catch(() => setAuth(false));
  }, [location.state]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
    setAuth(false);
  };

  if (auth === null) return <div className="min-h-screen bg-[#0B0A08]" />;

  if (!auth) {
    return (
      <div className="min-h-screen bg-[#0B0A08] text-[#F7F4EE] flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center" data-testid="admin-login">
          <SunMark className="w-20 h-auto mx-auto mb-8" />
          <h1 className="font-display text-4xl">Area riservata</h1>
          <p className="mt-4 text-sm text-[#CFC6B6]">Accesso consentito solo agli account Google autorizzati di TSM WAY.</p>
          {denied && <p className="mt-4 text-sm text-red-300" data-testid="admin-denied">Questo account Google non è autorizzato.</p>}
          <button onClick={login} className="btn-gold mt-8" data-testid="admin-google-login-btn">Accedi con Google</button>
          <div className="mt-6"><Link to="/" className="text-xs text-[#E6C594] underline underline-offset-4" data-testid="admin-back-home">Torna al sito</Link></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0A08] text-[#F7F4EE]" data-testid="admin-dashboard">
      <header className="border-b border-[#D4AF37]/15">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 h-20 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-home-link"><SunMark className="w-10 h-8" /><span className="font-display tracking-[0.3em] text-gold-grad">TSM WAY</span></Link>
          <div className="flex items-center gap-4 min-w-0">
            <span className="hidden sm:block text-xs text-[#CFC6B6] truncate" data-testid="admin-user-email">{user?.email}</span>
            <button onClick={logout} className="btn-ghost !py-2 !px-3" data-testid="admin-logout-btn"><LogOut size={14} /> Esci</button>
          </div>
        </div>
      </header>
      <main className="max-w-[1440px] mx-auto px-5 md:px-10 py-10 grid lg:grid-cols-2 gap-8 [&>*]:min-w-0">
        <Quotes />
        <Payments />
        <TripDesigner />
      </main>
    </div>
  );
}
